import React from "react";
import { FieldGroupRow, FieldGroupRowStatus } from "../../../models/intake";
import FormConstants from "./DataEntryFieldsConstants";
import { writeFieldToForm, ClassicFieldMeta, toDropdownFieldMeta } from "./formFieldHelpers";
import DropdownField, { DropdownOption } from "../../../common/formFields/DropdownField";
import { generatePickerOptions } from "../../../common/utils/appUtils";
import { DEFAULT_DROPDOWN_VALUE } from "../../../common/constants/ApiConstants";

export interface ClinicResponsibleFieldProps {
    /** Currently selected clinic/department id (read from DATA_ENTRY_FIELDS_FORM). */
    readonly value?: number;
    /** props.transactionDetails.facility.useClinics/useDepartments - drives both visibility and the label wording. */
    readonly useClinics: boolean;
    readonly useDepartments: boolean;
    /** props.discloseInstitutions, options built the same way the classic pane already does via generatePickerOptions. */
    readonly discloseInstitutions?: any;
    readonly dispatch: Function;
    readonly formName?: string;
    /**
     * Extra, non-form side effect the caller wants to run on change (the
     * classic pane's autosave PATCH). The base DATA_ENTRY_FIELDS_FORM write
     * is always handled by this component itself via writeFieldToForm.
     */
    readonly onClinicResponsibleChange?: (newValue: number) => void;
    /**
     * Real redux-form validation state, threaded down from
     * DataEntryDetailsRightPane.tsx (Spec #34102 / F1d regression fix -
     * restores "Clinic is required."/"Department is required." text on a
     * blocked Next-click). Omitted by Quick Entry's own call site.
     */
    readonly meta?: ClassicFieldMeta;
}

/**
 * Shared Clinic/Facility field (Spec #34098 / F1a). Only rendered when the
 * facility actually uses clinics or departments - mirrors the classic
 * pane's own visibility gate exactly (it renders an empty placeholder div
 * instead of nothing there, for its flex layout; the caller decides
 * whether Quick Entry needs an equivalent placeholder or can simply omit
 * the row).
 *
 * Bug fix (direct user correction, 2026-07-16): a previous change swapped
 * this to a Typeahead search combobox based on a misattributed bug report -
 * the actual "closes after typing a single character" report was about
 * Case Number, not this field. Classic Mode has always rendered this as a
 * plain DropdownField (confirmed by checking DataEntryDetailsRightPane.tsx's
 * own call site, which is unaffected either way since it's the same shared
 * component) - reverted to that, matching Classic Mode's real behavior.
 */
const ClinicResponsibleField: React.FC<ClinicResponsibleFieldProps> = (props) => {
    if (!props.useClinics && !props.useDepartments) {
        return null;
    }

    const formName = props.formName ?? FormConstants.DATA_ENTRY_FIELDS_FORM;
    // generatePickerOptions returns InormalizedType[] ({[key: string]: any}),
    // which is a superset of DropdownOption's shape at runtime but not
    // structurally assignable to it - normalize explicitly.
    // AB#35070 rework: "- Select -" (DEFAULT_DROPDOWN_VALUE) stays a normal, selectable option -
    // clearing a clinic back to "nothing chosen" is a legitimate thing to want to do. What the card
    // asks for is that you cannot CONFIRM the row while it sits there, and that is enforced two
    // ways: mapClinicResponsibleToFieldGroupRow reports NeedsEntry for the sentinel (below), and
    // Quick Entry disables the row's Confirm button for a NeedsEntry row (DataEntryDetails).
    const options: readonly DropdownOption[] = generatePickerOptions(props.discloseInstitutions, "name", "id")
        .map(option => ({ label: option.label, value: option.value }));

    const onChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = parseInt(event.target.value, 10);
        writeFieldToForm(props.dispatch, FormConstants.CLINIC_RESPONISBLE, newValue, formName);
        props.onClinicResponsibleChange?.(newValue);
    };

    return (
        <DropdownField
            label={props.useClinics ? "What clinic is responsible for this request?" : "Department responsible for this request:"}
            required
            meta={toDropdownFieldMeta(props.meta)}
            options={options}
            onChange={onChange}
            value={props.value}
        />
    );
};

export default ClinicResponsibleField;

export const CLINIC_RESPONSIBLE_ROW_ID = "clinicResponsible";

/**
 * Builds the FieldGroupRow for the Clinic/Facility row - always required when
 * visible. `aiName` is the AI-extracted clinic name (requestAnalysis.data.
 * clinicName); DataEntryDetails resolves it to a clinic ID and seeds the form,
 * so the row reads as AI-pre-selected (green) while the selected clinic's name
 * still matches the extracted name, flipping to user-entered once changed.
 */
export function mapClinicResponsibleToFieldGroupRow(
    badgeNumber: number,
    useClinics: boolean,
    formValue: number | undefined,
    discloseInstitutions?: any,
    aiName?: string,
): FieldGroupRow {
    // AB#35070 rework (QA): a real clinic id is a positive database id. The dropdown's own
    // "- Select -" writes DEFAULT_DROPDOWN_VALUE (-9999) through parseInt, and a cleared/replaced
    // document writes "" or 0 - none of those is a clinic. The old test (not undefined, not null)
    // accepted -9999, so picking "- Select -" flipped the row to Confirmed, the by-id lookup found
    // nothing, and the specialist was left with a CONFIRMED but BLANK Clinic row that Confirm
    // accepted. Requiring a positive id makes the row read NeedsEntry instead, which also makes
    // Confirm a safe no-op: isEditing keeps any row whose status isn't Confirmed open.
    const numericValue = Number(formValue);
    const hasValue = formValue !== undefined && formValue !== null && (formValue as any) !== ""
        && Number.isFinite(numericValue) && numericValue > 0;
    // AB#35070: resolve the clinic by its real `.id`, not by array index/key.
    // discloseInstitutions is an ARRAY (DataEntryDetailsDucks) and the dropdown's
    // option values are each item's `.id` (generatePickerOptions(..., "id")), so a
    // manual pick writes the real id into the form. The old `discloseInstitutions?.[formValue]`
    // was an index lookup that only coincidentally matched when id === array position,
    // so after a manual edit it missed and the row fell back to String(formValue) -
    // showing the raw numeric id instead of the clinic name. Find-by-id matches how
    // the dropdown builds its options and how the rest of the app resolves it.
    const institutionList: readonly any[] = Array.isArray(discloseInstitutions)
        ? discloseInstitutions
        : discloseInstitutions ? Object.values(discloseInstitutions) : [];
    const matched = hasValue
        ? institutionList.find((inst: any) => Number(inst?.id) === Number(formValue))
        : undefined;
    const isAiPreSelected = Boolean(aiName) && Boolean(matched?.name) &&
        matched.name.trim().toLowerCase() === aiName!.trim().toLowerCase();

    return {
        id: CLINIC_RESPONSIBLE_ROW_ID,
        badgeNumber,
        status: hasValue ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.NeedsEntry,
        label: useClinics ? "Clinic" : "Department",
        // AB#35070 (residual): show the resolved name or NOTHING - never String(formValue).
        // Resolving by real id (above) fixed the reported repro, but this fallback still
        // leaked the internal id whenever the lookup came up empty, which happens for real:
        // on reopen, quickEntryRehydration seeds the id from transactionDetails while
        // discloseInstitutions is still in flight, so every render in that window printed
        // the raw id; and a delisted clinic is never in the list at all. An internal
        // database id is not a value a specialist can act on. The row still reads as
        // Confirmed - a clinic IS selected - it just has no name to show yet, and picks one
        // up as soon as the list arrives.
        value: matched?.name ?? null,
        isAiPreSelected,
    };
}
